import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { BaseButton } from "./Button";
import { brandBackground, defaultFocusVisibleState } from "./buttonColors";

const variants = {
    outline: {
        bg: tokens.colors.surfaces["container-background"],
        border: tokens.colors.surfaces["border-divider"],
        fg: tokens.colors.text.primary,
        hoverBg: tokens.colors.neutral.grey.Alpha["10"],
    },
    solid: {
        bg: brandBackground("--button-color", tokens.colors.brand["aie-blue"]["500"]),
        border: brandBackground("--button-color", tokens.colors.brand["aie-blue"]["500"]),
        fg: tokens.colors.neutral.white,
        hoverBg: brandBackground("--button-hover-color", tokens.colors.brand["aie-blue"]["600"]),
        activeBg: brandBackground("--button-active-color", tokens.colors.brand["aie-blue"]["700"]),
    },
    destructive: {
        bg: tokens.colors.ui.destructive,
        border: tokens.colors.ui.destructive,
        fg: tokens.colors.neutral.white,
        hoverBg: tokens.colors.states.alert.default,
    },
    ghost: {
        bg: "transparent",
        border: "transparent",
        fg: tokens.colors.text.primary,
        hoverBg: tokens.colors.neutral.grey.Alpha["10"],
    },
};

const StyledButton = styled(BaseButton)(({ $variant = "outline", $size = 35 }) => {
    const variant = variants[$variant] || variants.outline;
    const size = typeof $size === "number" ? `${$size}px` : $size;

    return {
        width: size,
        height: size,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        background: variant.bg,
        border: `${tokens.globals["spacing-px"]} solid ${variant.border}`,
        borderRadius: tokens.globals["border-radius"],
        boxShadow: $variant === "ghost" ? "none" : "0px 1px 2px 0px rgba(0, 0, 0, 0.10)",
        color: variant.fg,
        cursor: "pointer",
        transition: "background 0.15s ease-in-out, border-color 0.15s ease-in-out",

        "&:hover:not(:disabled)": {
            background: variant.hoverBg,
            borderColor: variant.hoverBg,
        },
        "&:active:not(:disabled)": variant.activeBg ? {
            background: variant.activeBg,
            borderColor: variant.activeBg,
            transform: "scale(0.96)",
        } : {},
        "&:focus-visible": defaultFocusVisibleState,
        "&:disabled": {
            opacity: 0.5,
            cursor: "not-allowed",
        },
    };
});

/**
 * Square icon-only AIE button (e.g. table row action buttons like open/unlink).
 * @param {string} props.icon Font Awesome icon name (without the weight prefix), e.g. "trash".
 * @param {string} [props.ariaLabel] Accessible name; falls back to `icon` when omitted, so pass a
 *   real label whenever the icon name alone wouldn't make sense to a screen reader.
 * Also: variant, size, disabled, disabledReason, title, onClick, iconClass (self-explanatory).
 */
const IconButton = forwardRef(function IconButton(
    {
        icon,
        variant = "outline",
        size = 35,
        disabled = false,
        ariaLabel,
        disabledReason,
        title,
        onClick,
        iconClass = "fal",
        ...props
    },
    ref
) {
    return (
        <StyledButton
            {...props}
            ref={ref}
            type="button"
            $variant={variant}
            $size={size}
            disabled={disabled}
            onClick={onClick}
            aria-label={ariaLabel || icon}
            title={disabled ? disabledReason : title}
        >
            <i className={`${iconClass} fa-${icon}`} aria-hidden="true" />
        </StyledButton>
    );
});

IconButton.displayName = "IconButton";

export default IconButton;

/**
 * Lays out a row of IconButtons (e.g. a table row's action column), right-aligned.
 */
export const StyledActionGroup = styled.div({
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.globals["spacing-2"],
});

/**
 * Wraps an IconButton in a router Link without adding its own layout/decoration,
 * so the link behaves like a plain IconButton click target.
 */
export const StyledIconLink = styled(Link)({
    display: "inline-flex",
    textDecoration: "none",
});
